import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  Heading,
  List,
  ListItem,
  Stack,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { chain, keys, map, orderBy, startCase } from 'lodash'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import useConfigList from '../../hooks/useConfigList'
import useBrands from '../../hooks/useBrands'
import useFirestoreUser from '../../hooks/useFirestoreUser'
import useIsSuperAdmin from '../../hooks/useIsSuperAdmin'
import useActiveUid from '../../hooks/useActiveUid'
import useReports from '../../hooks/useReports'
import useStagesCount from '../../hooks/useStagesCount'
import { alsfrs } from '../../questionnaires/alsfrs'
import { alssq } from '../../questionnaires/alssq'
import useStore, { Reports, waterTypes } from '../../store/useStore'
import FirestoreUser, { diagnosisTitles } from '../../types/FirestoreUser'
import { maxQuestionnaireScore } from '../../types/Questionnaire'
import {
  Regimen,
  amountUnits,
  durationUnits,
  frequencyUnits,
  methods,
  percentages,
} from '../../types/Regimen'
import {
  StoolQuestionnaire,
  giSymptoms,
  stoolTypes2,
  stoolVolumes,
  urineColors,
} from '../../types/StoolQuestionnaire'
import formatMoment from '../../utils/formatMoment'
import { formatMonth } from '../../utils/formatMonth'
import { FamilySupportTable } from '../Report/Step-13/components/FamilySupport'
import { MedicalSupportTable } from '../Report/Step-13/components/MedicalSupport'
import Bootcamps from '../Report/Step-12/components/Bootcamps'
import LiveSessions from '../Report/Step-12/components/LiveSessions'
import StagesTable from '../Report/Step-12/components/StagesTable'
import { VitalStatsTable } from '../Report/Step-5'
import PANAS from './Panas'
import EssentialsCourses from '../Report/Step-12/components/EssentialsCourses'
import MacroIntake from './MacrosIntake'
import DietQuestions from './DietQuestions'
import { useReportingForParam } from '../../hooks/useReportingForMonth'
import PrintAccordion from './components/PrintAccordion'
import useAccordionPreference from './hooks/useAccordionPreference'
import { useLabs, useUserLabs } from '../../data/labsCollection'
import PrintLabResults, {
  formatLabTypeTitle,
  PrintLabRecord,
} from './components/PrintLabResults'

export const PrintContent = ({
  uid,
  user,
  reports,
  regimens,
}: {
  uid: string
  user: FirestoreUser
  reports: Reports
  regimens: Regimen[]
}) => {
  const [state, setState] = useState<{ [id: string]: boolean }>({
    medical: true,
  })
  const [onlyShowLabResultsWithValues, setOnlyShowLabResultsWithValues] =
    useState(true)
  const { expandAllAccordions, setExpandAllAccordions } =
    useAccordionPreference()
  const [labTemplates] = useLabs({ isArchived: false })
  const [userLabSnapshots, userLabsFound] = useUserLabs({
    userId: uid,
    isArchived: false,
  })

  const activeLabRecords = useMemo<PrintLabRecord[]>(
    () =>
      (userLabSnapshots ?? [])
        .filter((lab) => !lab.data().archived)
        .map((lab) => ({ id: lab.id, ...lab.data() })),
    [userLabSnapshots],
  )
  const labTypes = useMemo(
    () =>
      (labTemplates ?? [])
        .filter((lab) => !lab.data().archived)
        .map((lab) => {
          const template = lab.data()
          return {
            id: lab.id,
            records: activeLabRecords.filter(
              (record) => record.title === template.title,
            ),
            template,
          }
        }),
    [activeLabRecords, labTemplates],
  )
  const hasLabs =
    userLabsFound === true && labTypes.some((lab) => lab.records.length > 0)

  const MyCheckbox = ({
    id,
    isDisabled = false,
    title,
  }: {
    id: string
    isDisabled?: boolean
    title: string
  }) => {
    return (
      <Checkbox
        isDisabled={isDisabled}
        isChecked={state[id]}
        onChange={(e) => {
          setState({
            ...state,
            [id]: e.target.checked,
          })
        }}
      >
        {title}
      </Checkbox>
    )
  }

  return (
    <Stack spacing={4}>
      <Stack spacing={0}>
        <Heading>Print Records</Heading>
        {user && <Subheading user={user} uid={uid} />}
      </Stack>
      <Box p={2} borderWidth={1} borderColor='gray.300' rounded='md'>
        <Stack spacing={0}>
          <Text fontSize='sm'>
            Which types of records do you want to include in this report?
          </Text>
          <MyCheckbox id='medical' title='Medical' />
          <MyCheckbox id='support' title='Support Team' />
          <MyCheckbox
            id='clinicalObservations'
            title='Journal - Clinical Observations'
          />
          <MyCheckbox
            id='healingAlsActivities'
            title='Healing ALS Activities'
          />
          {labTypes.map((lab) => (
            <MyCheckbox
              key={lab.id}
              id={`lab-${lab.id}`}
              isDisabled={userLabsFound !== true || lab.records.length === 0}
              title={formatLabTypeTitle(lab.template.title)}
            />
          ))}
          <Stack align='center' direction='row' pl={6} spacing={3}>
            <Text fontSize='sm'>Only show lab tests with results</Text>
            <Switch
              aria-label='Only show lab tests with results'
              colorScheme='purple'
              isChecked={onlyShowLabResultsWithValues}
              isDisabled={!hasLabs}
              onChange={(e) => {
                setOnlyShowLabResultsWithValues(e.target.checked)
              }}
            />
          </Stack>
        </Stack>
      </Box>
      <Box>
        <Button
          onClick={() => {
            window.print()
          }}
        >
          Print or Save to PDF
        </Button>
      </Box>
      <Box display='flex' justifyContent='flex-end'>
        <Stack align='center' direction='row' spacing={3}>
          <Text fontSize='sm'>Open all sections by default</Text>
          <Switch
            aria-label='Open all sections by default'
            colorScheme='purple'
            isChecked={expandAllAccordions}
            onChange={(e) => {
              setExpandAllAccordions(e.target.checked)
            }}
          />
        </Stack>
      </Box>
      {state.medical && (
        <MedicalRecords
          expandAllAccordions={expandAllAccordions}
          reports={reports}
          regimens={regimens}
          user={user}
        />
      )}
      {state.clinicalObservations && (
        <ClinicalObservations
          expandAllAccordions={expandAllAccordions}
          reports={reports}
        />
      )}
      {state.support && (
        <PrintSupport
          expandAllAccordions={expandAllAccordions}
          reports={reports}
        />
      )}
      {state.healingAlsActivities && (
        <HealingALSActivities
          expandAllAccordions={expandAllAccordions}
          reports={reports}
          user={user}
        />
      )}
      {labTypes.map(
        (lab) =>
          state[`lab-${lab.id}`] &&
          lab.records.length > 0 && (
            <PrintLabResults
              key={lab.id}
              expandAll={expandAllAccordions}
              labType={formatLabTypeTitle(lab.template.title)}
              onlyWithResults={onlyShowLabResultsWithValues}
              records={lab.records}
              template={lab.template}
            />
          ),
      )}
    </Stack>
  )
}

export default () => {
  const uid = useActiveUid()
  const user = useFirestoreUser()
  const reports = useReports()
  const regimens = useStore((state) => state.regimens || [])

  if (!uid || !user) return null

  return (
    <PrintContent user={user} reports={reports} regimens={regimens} uid={uid} />
  )
}

const MedicalRecords = ({
  expandAllAccordions,
  reports,
  regimens,
  user,
}: {
  expandAllAccordions: boolean
  reports: Reports
  regimens: Regimen[]
  user: FirestoreUser
}) => {
  // const count = useStagesCount()
  return (
    <PrintAccordion expandAll={expandAllAccordions}>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            ALSFRS Scores
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <PrintQuestionnaire type='alsfrs' reports={reports} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            ALSSQ Scores
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <PrintQuestionnaire type='alssq' reports={reports} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            PANAS - Main
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <PANAS panasType='main' reports={reports} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            PANAS - Supplemental
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <PANAS panasType='supplemental' reports={reports} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            PANAS - Modified
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <PANAS panasType='modifiedPanas' reports={reports} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Diets
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <PrintRegimens title='Diets' list='diets' regimens={regimens} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Water Intake
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <WaterIntake reports={reports} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Macro Intake
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <MacroIntake reports={reports} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Diet Questions
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <DietQuestions reports={reports} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Supplements
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <Stack spacing={2}>
            <PrintRegimens
              title='Current'
              list='supplements'
              regimens={regimens}
              brandFieldId='brandId'
              showCurrent
              isSubheading
            />
            <PrintRegimens
              title='Past'
              list='supplements'
              regimens={regimens}
              brandFieldId='brandId'
              showPast
              isSubheading
            />
          </Stack>
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Medications
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <Stack spacing={2}>
            <PrintRegimens
              title='Current'
              list='nonSupplementMedications'
              regimens={regimens}
              brandFieldId='brandId'
              showCurrent
              isSubheading
            />
            <PrintRegimens
              title='Past'
              list='nonSupplementMedications'
              regimens={regimens}
              brandFieldId='brandId'
              showPast
              isSubheading
            />
          </Stack>
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Protocols
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <PrintRegimens
            title='Protocols'
            list='protocols'
            regimens={regimens}
          />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Physical Strategies
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <Stack spacing={2}>
            <PrintRegimens
              title='Current'
              list='physicalStrategies'
              regimens={regimens}
              showCurrent
              isSubheading
            />
            <PrintRegimens
              title='Past'
              list='physicalStrategies'
              regimens={regimens}
              showPast
              isSubheading
            />
          </Stack>
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Dental
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <PrintRegimens
            title='Dental'
            list='dentalProcedures'
            regimens={regimens}
          />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Mind Strategies
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <Stack spacing={2}>
            <PrintRegimens
              title='Current'
              list='mentalStrategies'
              regimens={regimens}
              showCurrent
              isSubheading
            />
            <PrintRegimens
              title='Past'
              list='mentalStrategies'
              regimens={regimens}
              showPast
              isSubheading
            />
          </Stack>
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Clinical Trials
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <PrintClinicalTrials additionalDetails={user.additionalDetails} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Vital Statistics
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <VitalStatsTable reports={reports} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Stool
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <Stool reports={reports} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Urine
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <Urine reports={reports} />
        </AccordionPanel>
      </AccordionItem>
    </PrintAccordion>
  )
}

const WaterIntake = ({ reports }: { reports: Reports }) => {
  return (
    <Stack>
      <TableContainer>
        <Table size='sm' variant='simple'>
          <Thead>
            <Tr>
              <Th>Month</Th>
              <Th>Water Type</Th>
              <Th>Water Amount (oz.)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {map(reports, (report, month) => {
              const m = moment(month + '-15')
              if (!report?.waterAmount && !report?.waterType) return null
              return (
                <Tr key={month}>
                  <Td>{m.format('MMM-YY')}</Td>
                  <Td>
                    {report?.waterType ? waterTypes[report.waterType] : '-'}
                  </Td>
                  <Td>{report?.waterAmount || '-'}</Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

const HealingALSActivities = ({
  expandAllAccordions,
  reports,
  user,
}: {
  expandAllAccordions: boolean
  reports: Reports
  user: FirestoreUser
}) => {
  const count = useStagesCount()

  return (
    <PrintAccordion expandAll={expandAllAccordions}>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Healing ALS Group Sessions
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <LiveSessions reports={reports} />
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Courses
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <Stack>
            <EssentialsCourses editable={false} user={user} />
            <Bootcamps editable={false} user={user} />
          </Stack>
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            {count} Steps To Healing
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <StagesTable user={user} />
        </AccordionPanel>
      </AccordionItem>
    </PrintAccordion>
  )
}

const ClinicalObservations = ({
  expandAllAccordions,
  reports,
}: {
  expandAllAccordions: boolean
  reports: Reports
}) => {
  const entries = orderBy(
    map(reports, (report, id) => {
      if (
        !report?.clinicalObservations ||
        typeof report.clinicalObservations === 'boolean'
      )
        return null

      let str = report.clinicalObservations
      while (str.includes('\n\n')) {
        str = str.replace('\n\n', '\n')
      }

      return { id, str }
    }).filter(Boolean),
    'id',
    'desc',
  )

  if (entries.length === 0) return null

  return (
    <PrintAccordion expandAll={expandAllAccordions}>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Journal - Clinical Observations
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <Stack spacing={4}>
            {entries.map((entry) => (
              <Stack key={entry!.id} spacing={1}>
                <Heading size='sm'>{entry!.id}</Heading>
                <Text whiteSpace='pre-line'>{entry!.str}</Text>
              </Stack>
            ))}
          </Stack>
        </AccordionPanel>
      </AccordionItem>
    </PrintAccordion>
  )
}

const Subheading = ({ user, uid }: { user: FirestoreUser; uid: string }) => {
  const isSuperAdmin = useIsSuperAdmin()
  const isMine = useActiveUid() === uid

  return (
    <>
      <Text fontSize='sm' fontWeight='normal'>
        Patient ID: #{user.patientId}
      </Text>
      <Text fontSize='sm' fontWeight='normal'>
        Name: {user.profile?.firstName + ' ' + user.profile?.lastName}
      </Text>
      <Text fontSize='sm' fontWeight='normal'>
        Email: {user.email}
      </Text>
      <Text fontSize='sm' fontWeight='normal'>
        Phone: {user.profile?.phoneNumber || 'N/A'}
      </Text>
      <Text fontSize='sm' fontWeight='normal'>
        Diagnosis:{' '}
        {user.profile?.diagnosis
          ? diagnosisTitles[user.profile.diagnosis]
          : 'N/A'}
      </Text>
      <Text fontSize='sm' fontWeight='normal'>
        Birth Date:{' '}
        {!user.profile?.birthDate
          ? 'N/A'
          : isSuperAdmin || isMine
            ? user.profile.birthDate
            : moment(user.profile.birthDate, 'YYYY-MM-DD').format('MM/YYYY')}
      </Text>
      <Text fontSize='sm' fontWeight='normal'>
        Diagnosis Date:{' '}
        {!user.profile?.diagnosisDate
          ? 'N/A'
          : isSuperAdmin || isMine
            ? user.profile.diagnosisDate
            : moment(user.profile.diagnosisDate, 'YYYY-MM-DD').format(
                'MM/YYYY',
              )}
      </Text>
      <Text fontSize='sm' fontWeight='normal'>
        Joined Registry:{' '}
        {!user.createdAt || typeof user.createdAt.toDate !== 'function'
          ? 'N/A'
          : isSuperAdmin || isMine
            ? moment(user.createdAt.toDate()).format('YYYY-MM-DD')
            : moment(user.createdAt.toDate()).format('MM/YYYY')}
      </Text>
      {isSuperAdmin && (
        <Link to={`/admin/users/${uid}`}>
          <Button variant='link'>Manage User</Button>
        </Link>
      )}
    </>
  )
}

const PrintQuestionnaire = ({
  type,
  reports,
}: {
  type: 'alsfrs' | 'alssq'
  reports: Reports
}) => {
  const scores: { key: string; score: number }[] = []

  map(reports, (value, key) => {
    const score = value?.[type]?.score
    if (score !== undefined) {
      scores.push({ key, score })
    }
  })

  return (
    <Stack spacing={0}>
      {scores.length > 0 ? (
        <List>
          {scores.map(({ key, score }) => {
            return (
              <ListItem key={key}>
                {formatMonth(key)}: {score}/
                {maxQuestionnaireScore(type === 'alsfrs' ? alsfrs : alssq)}
              </ListItem>
            )
          })}
        </List>
      ) : (
        <Text>No records.</Text>
      )}
    </Stack>
  )
}

const Stool = ({ reports }: { reports: Reports }) => {
  const data = useMemo(() => {
    const data: {
      key: string
      stool: Partial<StoolQuestionnaire['stool']>
    }[] = []
    map(reports, (value, key) => {
      const stool = value?.stool?.stool
      if (stool?.complete) {
        data.push({
          key,
          stool,
        })
      }
    })

    return data
  }, [reports])

  return (
    <Stack spacing={4}>
      <Stack>
        <TableContainer>
          <Table size='sm' variant='unstyled'>
            <Thead>
              <Tr>
                <Th>Month</Th>
                <Th>Type</Th>
                <Th>Color</Th>
                <Th>Odor</Th>
                <Th>Volume</Th>
                <Th>Floats?</Th>
                <Th>Frequency</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.map(({ key, stool }) => {
                return (
                  <Tr key={key}>
                    <Td>{formatMonth(key)}</Td>
                    <Td>
                      {stool.type !== undefined ? stoolTypes2[stool.type] : ''}
                    </Td>
                    <Td>{stool.color ? startCase(stool.color) : ''}</Td>
                    <Td>{stool.odor ? startCase(stool.odor) : ''}</Td>
                    <Td>
                      {stool.volume !== undefined
                        ? stoolVolumes[stool.volume]
                        : ''}
                    </Td>
                    <Td>
                      {stool.float === undefined
                        ? ''
                        : stool.float
                          ? 'Yes'
                          : 'No'}
                    </Td>
                    <Td>
                      {stool.daily === undefined
                        ? ''
                        : stool.daily
                          ? `${stool.times}/day`
                          : `${stool.times}/week`}
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Stack>
      <Stack>
        <Heading size='sm'>GI Symptoms</Heading>
        <TableContainer>
          <Table size='sm' variant='unstyled'>
            <Thead>
              <Tr>
                <Th>Month</Th>
                <Th>Symptoms</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.map(({ stool, key }) => {
                return (
                  <Tr key={key}>
                    <Td>{formatMonth(key)}</Td>
                    <Td>
                      {stool.giSymptoms?.length
                        ? stool.giSymptoms
                            .map((s) => {
                              const severity = stool.giSymptomsSeverity?.find(
                                (sev) => sev.giSymptom === s,
                              )?.severity
                              return severity && s !== 'none'
                                ? `${giSymptoms[s]} (${severity})`
                                : giSymptoms[s]
                            })
                            .join(', ')
                        : ''}
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Stack>
    </Stack>
  )
}

const Urine = ({ reports }: { reports: Reports }) => {
  const data = useMemo(() => {
    const data: {
      key: string
      urine: Partial<StoolQuestionnaire['urine']>
    }[] = []
    map(reports, (value, key) => {
      const urine = value?.stool?.urine
      if (urine?.complete) {
        data.push({
          key,
          urine,
        })
      }
    })

    return data
  }, [reports])

  return (
    <Stack>
      <TableContainer>
        <Table size='sm' variant='unstyled'>
          <Thead>
            <Tr>
              <Th>Month</Th>
              <Th>Frequency</Th>
              <Th>Color</Th>
              <Th>Odor</Th>
            </Tr>
          </Thead>
          <Tbody>
            {data.map(({ key, urine }) => {
              return (
                <Tr key={key}>
                  <Td>{formatMonth(key)}</Td>
                  <Td>
                    {urine.timesDaily === undefined
                      ? ''
                      : `${urine.timesDaily}/day`}
                  </Td>
                  <Td>{urine.color ? urineColors[urine.color] : ''}</Td>
                  <Td>{urine.odor ? startCase(urine.odor) : ''}</Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

const PrintRegimens = ({
  list,
  title,
  regimens: _regimens,
  brandFieldId,
  showCurrent,
  showPast,
  isSubheading,
}: {
  list: string
  title: string
  regimens: Regimen[]
  brandFieldId?: string
  showCurrent?: boolean
  showPast?: boolean
  isSubheading?: boolean
}) => {
  const config = useConfigList(list)
  const brands = useBrands()
  let regimens = _regimens.filter((r) => r.list === list)

  // Filter by current or past if specified
  if (showCurrent) {
    regimens = regimens.filter((r) => !r.endDate)
  } else if (showPast) {
    regimens = regimens.filter((r) => r.endDate)
  }

  return (
    <Stack spacing={0}>
      {isSubheading && (
        <Heading size='sm' fontStyle='italic'>
          {title}
        </Heading>
      )}
      {regimens.length === 0 ? (
        <Text>No records.</Text>
      ) : (
        <List>
          {orderBy(
            regimens,
            (r) => {
              return config[r.primaryId]
            },
            'asc',
          ).map((regimen) => {
            return (
              <ListItem key={regimen.id}>
                {config[regimen.primaryId]}
                {brandFieldId &&
                regimen.usages.length > 0 &&
                regimen.usages[0].fields[brandFieldId]
                  ? ` - ${brands[regimen.usages[0].fields[brandFieldId]]}`
                  : ''}
                :{' '}
                {orderBy(regimen.usages, 'startedAt', 'desc')
                  .map((u) => {
                    return `${formatMoment(moment(u.startDate))}
                    ${u.percentage ? ` ${percentages[u.percentage]}` : ''}${
                      u.amount && u.amountUnit
                        ? ` ${u.amount}${amountUnits[u.amountUnit]}`
                        : ''
                    }${
                      u.frequency && u.frequencyUnit
                        ? ` ${u.frequency}x/${frequencyUnits[u.frequencyUnit]}`
                        : ''
                    }${
                      u.duration && u.durationUnit
                        ? ` ${u.duration} ${durationUnits[u.durationUnit]}`
                        : ''
                    }${u.method ? ` (${methods[u.method]})` : ''}`
                  })
                  .join(', ')}
              </ListItem>
            )
          })}
        </List>
      )}
    </Stack>
  )
}

const PrintSupport = ({
  expandAllAccordions,
  reports: _reports,
}: {
  expandAllAccordions: boolean
  reports: Reports
}) => {
  const reports = chain(_reports)
    .map((report, id) => ({ id, ...report }))
    .orderBy('id', 'desc')
    .value()
  const reportingFor = useReportingForParam()

  const entries = reports
    .map((report) => {
      const people = report.emotionalSupportPeople || []
      const doctorIds = keys(report.medicalSupport).filter(
        (doctorId) => !!report.medicalSupport?.[doctorId],
      )
      if (people.length === 0 && doctorIds.length === 0) return null

      return { report, people, doctorIds }
    })
    .filter(Boolean)

  if (entries.length === 0) return null

  return (
    <PrintAccordion expandAll={expandAllAccordions}>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            Support Team
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <Stack spacing={4}>
            {entries.map((entry) => (
              <Stack key={entry!.report.id} spacing={2}>
                <Heading size='sm'>{entry!.report.id}</Heading>
                {entry!.people.length > 0 && (
                  <Stack spacing={1}>
                    <Heading fontSize='sm'>
                      Emotional & Physical Support
                    </Heading>
                    <FamilySupportTable people={entry!.people} />
                  </Stack>
                )}
                {entry!.doctorIds.length > 0 && (
                  <Stack spacing={1}>
                    <Heading fontSize='sm'>Medical Support</Heading>
                    <MedicalSupportTable
                      medicalSupport={entry!.report.medicalSupport || {}}
                      reportingFor={reportingFor}
                      reportingMonth={reportingFor}
                    />
                  </Stack>
                )}
              </Stack>
            ))}
          </Stack>
        </AccordionPanel>
      </AccordionItem>
    </PrintAccordion>
  )
}

const PrintClinicalTrials = ({
  additionalDetails,
}: {
  additionalDetails: FirestoreUser['additionalDetails']
}) => {
  const clinicalTrials = additionalDetails?.clinicalTrials || []

  return (
    <Stack spacing={0}>
      {clinicalTrials.length === 0 ? (
        <Text>No records.</Text>
      ) : (
        <TableContainer>
          <Table size='sm' variant='unstyled'>
            <Thead>
              <Tr>
                <Th>Study</Th>
                <Th>Start Date</Th>
                <Th>End Date</Th>
                <Th>Group</Th>
              </Tr>
            </Thead>
            <Tbody>
              {clinicalTrials.map((trial) => (
                <Tr key={trial.id}>
                  <Td>{trial.study}</Td>
                  <Td>{trial.startDate}</Td>
                  <Td>{trial.endDate || 'Current'}</Td>
                  <Td>{trial.group || 'N/A'}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  )
}
